import React, { memo, useCallback } from 'react';
import type { Node } from 'react';
import styled from '@emotion/styled';
import { darken, shade, tint, transparentize } from 'polished';
import { themeProp } from '@vmos2/utils';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';

import { useComponentTheme } from '../useComponentTheme';
import { IconButton } from './IconButton';
import { Icon } from '../icons';
import { withSpacingStyles } from '../withSpacingStyles';
import { useAccessibilityContext } from '@vmos2/providers';
import { designTokens } from '../designTokens';

type Props = {
  ariaDescribedBy?: string,
  ariaDisabled: boolean,
  ariaLabel?: string,
  ariaLabelledBy?: string,
  ariaPressed?: boolean,
  block?: boolean,
  boxShadow?: 'small' | 'medium' | 'large' | 'center' | 'focus',
  buttonRef?: { current: HTMLElement | null },
  children: Node,
  color?: 'dark',
  contentCenter?: boolean,
  dataTest?: ?string,
  disabled?: boolean,
  hasDropdownArrow?: boolean,
  height?: string,
  iconLeft?: Node | string,
  iconName?: string,
  iconRight?: Node,
  iconTop?: Node | string,
  isActive?: boolean,
  isIconOnly?: boolean,
  justifyContent?: string,
  mode?: 'primary' | 'danger' | 'outline' | 'light' | 'custom' | 'nav' | 'link' | 'ghost',
  noWrap?: boolean,
  onClick?: Function,
  rounded?: boolean,
  size: 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall',
  tooltip?: string,
  touchTooltip?: boolean,
  type?: 'submit' | 'button' | 'reset',
};

const buttonStyles = {
  xxlarge: {
    padding: '36px 36px',
    iPadKioskPadding: '36px 36px',
    largeScreenKioskPadding: '36px 36px',
    fontSize: theme => theme.fontSize.xl,
  },
  xlarge: {
    padding: '12px 16px',
    iPadKioskPadding: '32px 24px',
    largeScreenKioskPadding: '32px 24px',
    fontSize: theme => theme.fontSize.xxl,
  },
  large: {
    padding: '12px 16px',
    iPadKioskPadding: '18px 24px',
    largeScreenKioskPadding: '18px 24px',
    fontSize: theme => theme.fontSize.xl,
  },
  medium: {
    padding: '10px 12px',
    iPadKioskPadding: '12px 16px',
    largeScreenKioskPadding: '18px 20px',
    fontSize: theme => theme.fontSize.md,
  },
  small: {
    padding: '10px 12px',
    iPadKioskPadding: '12px 16px',
    largeScreenKioskPadding: '18px 24px',
    fontSize: theme => theme.fontSize.md,
  },
  xsmall: {
    padding: '8px 10px',
    iPadKioskPadding: '8px 10px',
    largeScreenKioskPadding: '8px 10px',
    fontSize: theme => theme.fontSize.sm,
  },
};

const defaultModes = ['primary', 'danger', 'outline', 'light', 'custom', 'nav', 'link', 'ghost'];

export const _Button = ({
  ariaDescribedBy,
  ariaDisabled = false,
  ariaLabel,
  ariaLabelledBy,
  ariaPressed,
  boxShadow,
  buttonRef,
  children,
  dataTest,
  disabled,
  hasDropdownArrow,
  iconLeft,
  iconName,
  iconRight,
  iconTop,
  isActive,
  isIconOnly,
  mode,
  noWrap,
  onClick,
  size,
  tooltip,
  touchTooltip,
  type,
  ...attributes
}: Props): Node => {
  // If it's necessary to override the styles of one of the default modes, a custom mode with a name that ends in one of
  // the default modes (capitalised) can be used, e.g. `HomepagePrimary` or `HomepageLight`. This will apply all of the
  // styles from the default mode, and then individual styles can be overriden as normal in the component theming.
  const { isHighContrast } = useAccessibilityContext();
  const modeKey = defaultModes.includes(mode) ? capitalize(mode) : mode;
  const modeType = modeKey.split(/(?=[A-Z])/).slice(-1)[0];
  const defaultComponentTheme = useComponentTheme(theme => theme.components.Button, modeType);
  const customComponentTheme = useComponentTheme(theme => theme.components.Button, modeKey);
  const componentTheme = { ...defaultComponentTheme, ...customComponentTheme };
  let Tag;

  switch (modeType.toLowerCase()) {
    case 'primary':
      Tag = PrimaryButton;
      break;
    case 'danger':
      Tag = DangerButton;
      break;
    case 'outline':
      Tag = OutlineButton;
      break;
    case 'light':
      Tag = LightButton;
      break;
    case 'custom':
      Tag = DefaultButton;
      break;
    case 'ghost':
      Tag = GhostButton;
      break;
    case 'link':
      Tag = NavButton;
      break;
    default:
      Tag = PrimaryButton;
      break;
  }

  // We disable the button via `aria-disabled`, rather than `disabled`, so that the element is still focusable by screen
  // readers. This means that we need manual checks to ensure interaction does not happen when it should be disabled.
  const isDisabled = disabled || ariaDisabled;

  const handleClick = useCallback(
    async e => {
      if (onClick && !isDisabled) {
        await onClick(e);
      }
    },
    [isDisabled, onClick]
  );

  if (isIconOnly) {
    return (
      <IconButton
        ariaDisabled={ariaDisabled}
        ariaLabel={ariaLabel}
        ariaPressed={ariaPressed}
        boxShadow={boxShadow}
        dataTest={dataTest}
        disabled={disabled}
        icon={iconName}
        mode={mode}
        onClick={onClick}
        ref={buttonRef}
        size={size}
        tooltip={tooltip}
        touchTooltip={touchTooltip}
        type={type}
        {...attributes}
      />
    );
  }

  return (
    <Tag
      {...attributes}
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-pressed={ariaPressed}
      boxShadow={boxShadow}
      componentTheme={componentTheme}
      dataTest={dataTest}
      disabled={disabled}
      iconTop={iconTop}
      isActive={isActive}
      isHighContrast={isHighContrast}
      noWrap={noWrap}
      ref={buttonRef}
      size={size}
      type={type}
      {...(ariaDisabled && { 'aria-disabled': ariaDisabled })}
      {...(onClick && { onClick: handleClick })}
    >
      {iconTop && <IconWrapper>{iconTop}</IconWrapper>}
      {iconLeft && <IconWrapper>{iconLeft}</IconWrapper>}
      {iconName && (
        <IconWrapper>
          <Icon name={iconName} />
        </IconWrapper>
      )}
      {children || tooltip}
      {iconRight && <IconWrapper>{iconRight}</IconWrapper>}
      {hasDropdownArrow && <DropdownArrow />}
    </Tag>
  );
};

export const Button = memo(withSpacingStyles(_Button));

const enabledSelector = ':enabled:not([aria-disabled="true"])';
const activeSelector = `&:active${enabledSelector}`;
const disabledSelector = '&:disabled, &[aria-disabled="true"]';
const hoverSelector = `&:hover${enabledSelector}`;

const DefaultButton = styled.button`
  display: inline-flex;
  justify-content: ${({ contentCenter, justifyContent }) =>
    justifyContent || (contentCenter ? 'center' : 'space-between')};
  gap: 12px 8px;
  padding: ${({ size }) => buttonStyles[size].padding};
  font-size: ${({ size, theme }) => buttonStyles[size].fontSize(theme)};
  width: ${({ block }) => (block ? '100%' : 'auto')};
  height: ${({ height }) => height};
  border-radius: ${({ theme, rounded }) => (rounded ? theme.borderRadius.ellipse : theme.borderRadius.medium)};
  font-weight: ${({ componentTheme, theme }) => componentTheme.fontWeight || theme.fontWeight.bold};
  align-items: center;
  cursor: pointer;
  background: transparent
    ${({ componentTheme: { backgroundImage } }) =>
      backgroundImage && ` url(${backgroundImage}) center / cover no-repeat`};
  transition: ${({ theme }) => theme.motion.transition};
  line-height: 1;
  font-family: ${({ theme, componentTheme }) =>
    componentTheme.secondaryFontFamily ? theme.typography.secondaryFontFamily : 'inherit'};
  text-decoration: ${({ componentTheme }) => componentTheme.textDecoration};
  text-transform: ${({ componentTheme }) => componentTheme.textTransform};
  white-space: ${({ noWrap }) => noWrap && 'nowrap'};
  position: relative;
  flex-direction: ${({ iconTop }) => iconTop && 'column'};
  box-shadow: ${({ boxShadow, theme }) => boxShadow && theme.boxShadow[boxShadow]};

  ${({ componentTheme: { hideContent } }) =>
    hideContent &&
    `
      & {
        color: transparent !important;
      }

      & > * {
        /* Setting it to a value higher than 0 allows for screen readers to be able to read the content, but we need to
          set it to a tiny number so that we can't see it. */
        opacity: 0.0001;
      }
    `}

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => `0 0 0 3px ${transparentize(0.5, theme.color.brand)}`};
  }

  ${disabledSelector} {
    cursor: not-allowed;
    color: ${({ theme }) => tint(0.6, theme.color.black)};
  }

  @media (${({ theme }) => theme.breakpoint.iPadKiosk}) {
    padding: ${({ size }) => buttonStyles[size].iPadKioskPadding};
  }

  @media (${({ theme }) => theme.breakpoint.largeScreenKiosk}) {
    padding: ${({ size }) => buttonStyles[size].largeScreenKioskPadding};
    ${({ size, theme }) => size === 'small' && `font-size: ${theme.fontSize.xl};`};
  }
`;

const PrimaryButton = styled(DefaultButton)`
  background-color: ${({ theme, componentTheme }) => componentTheme.backgroundColor || theme.color.brand};
  color: ${({ componentTheme }) => componentTheme.color};
  border: ${({ theme, componentTheme }) => `2px solid ${componentTheme.borderColor || theme.color.brand}`};
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};

  ${hoverSelector} {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.05, `${componentTheme.backgroundColor || theme.color.brand}`)};
    border-color: ${({ theme, componentTheme }) => shade(0.05, get(componentTheme, 'borderColor', theme.color.brand))};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  ${activeSelector} {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.08, `${componentTheme.backgroundColor || theme.color.brand}`)};
    color: ${({ componentTheme }) => componentTheme.color};
    border-color: ${({ theme, componentTheme }) => shade(0.08, get(componentTheme, 'borderColor', theme.color.brand))};
  }

  ${disabledSelector} {
    background-color: ${({ theme, isHighContrast }) =>
      isHighContrast ? designTokens.color.lightGrey : tint(0.6, theme.color.lightGrey)};
    border-color: ${({ theme, isHighContrast }) =>
      isHighContrast ? designTokens.color.lightGrey : tint(0.6, theme.color.lightGrey)};
    ${({ isHighContrast }) => (isHighContrast ? `color: ${designTokens.color.grey};` : '')};
  }
`;

const DangerButton = styled(DefaultButton)`
  background-color: ${({ theme }) => theme.color.danger};
  color: ${({ theme }) => theme.color.white};
  border: ${({ theme }) => `2px solid ${theme.color.danger}`};
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};

  ${hoverSelector} {
    background-color: ${({ theme }) => shade(0.05, `${theme.color.danger}`)};
  }

  ${activeSelector} {
    background-color: ${({ theme }) => shade(0.08, `${theme.color.danger}`)};
  }

  ${disabledSelector} {
    background-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
    border-color: ${({ theme }) => tint(0.6, theme.color.lightGrey)};
  }
`;

const OutlineButton = styled(DefaultButton)`
  background-color: ${({ componentTheme }) => componentTheme.backgroundColor};
  border: ${({ theme, componentTheme }) => `2px solid ${componentTheme.borderColor || theme.color.brand}`};
  color: ${({ theme, componentTheme }) => componentTheme.color || theme.color.brand};
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};

  ${hoverSelector} {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.05, `${componentTheme.backgroundColor || theme.color.brand}`)};
    border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
    color: ${({ theme, componentTheme }) => componentTheme.color || theme.color.brand};
  }

  ${activeSelector} {
    background-color: ${({ theme, componentTheme }) =>
      shade(0.08, componentTheme.backgroundColor || theme.color.brand)};
    border-color: ${({ theme, componentTheme }) => componentTheme.borderColor || theme.color.brand};
    color: ${({ theme, componentTheme }) => componentTheme.color || theme.color.brand};
  }

  ${disabledSelector} {
    background-color: transparent;
    border-color: ${({ theme, isHighContrast }) =>
      isHighContrast ? designTokens.color.lightGrey : theme.color.lightGrey};
  }
`;

const LightButton = styled(DefaultButton)`
  background-color: ${({ componentTheme }) => componentTheme.backgroundColor};
  border: ${({ componentTheme }) => `2px solid ${componentTheme.borderColor}`};
  color: ${({ componentTheme }) => componentTheme.color};
  border-radius: ${({ componentTheme }) => componentTheme.borderRadius};

  ${hoverSelector} {
    background-color: ${({ componentTheme }) => darken(0.25, componentTheme.backgroundColor)};
    border-color: ${({ componentTheme }) => componentTheme.borderColor};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  ${activeSelector} {
    background-color: ${({ componentTheme }) => darken(0.35, componentTheme.backgroundColor)};
    border-color: ${({ componentTheme }) => componentTheme.borderColor};
    color: ${({ componentTheme }) => componentTheme.color};
  }

  ${disabledSelector} {
    background-color: ${({ theme, isHighContrast }) =>
      isHighContrast ? designTokens.color.lightGrey : tint(0.6, theme.color.lightGrey)};
    border-color: ${({ theme, isHighContrast }) =>
      isHighContrast ? designTokens.color.lightGrey : tint(0.6, theme.color.lightGrey)};
    ${({ isHighContrast }) => (isHighContrast ? `color: ${designTokens.color.grey};` : '')};
  }

  &[aria-pressed='true']${enabledSelector} {
    background-color: ${({ componentTheme: { selected }, theme }) =>
      selected.backgroundColor || tint(0.85, theme.color.brand)};
    border-color: ${({ componentTheme: { selected }, theme }) => selected.borderColor || theme.color.brand};
    color: ${({ componentTheme: { selected }, theme }) => selected.color || theme.color.brand};
  }
`;

const NavButton = styled(DefaultButton)`
  font: inherit;
  font-weight: ${themeProp('fontWeight.regular')};
  color: ${({ color, theme, componentTheme }) =>
    componentTheme.color || (color ? (color === 'dark' ? theme.color.black : color) : theme.color.brand)};
  background-color: transparent;
  border: 0;
  padding: 0 !important;
  border-radius: 0;
  text-transform: none;

  &:focus {
    box-shadow: none;
  }
`;

const GhostButton = styled(DefaultButton)`
  color: ${({ color, theme, componentTheme }) =>
    componentTheme.color || (color ? (color === 'dark' ? theme.color.grey : color) : theme.color.brand)};
  background-color: transparent;
  border: 1px solid transparent;

  ${hoverSelector} {
    background-color: ${({ theme }) => transparentize(0.95, theme.color.black)};
    border-color: ${({ theme }) => transparentize(0.95, theme.color.black)};
    color: ${({ color, theme, componentTheme }) =>
      componentTheme.color || (color ? (color === 'dark' ? theme.color.grey : color) : theme.color.brand)};
  }

  ${activeSelector} {
    background-color: ${({ theme }) => transparentize(0.92, theme.color.black)};
    border-color: ${({ theme }) => transparentize(0.92, theme.color.black)};
    color: ${({ color, theme, componentTheme }) =>
      componentTheme.color || (color ? (color === 'dark' ? theme.color.grey : color) : theme.color.brand)};
  }
`;

const IconWrapper = styled('span')`
  display: inherit;
  fill: currentColor;
  pointer-events: none;
`;

const DropdownArrow = styled('div')`
  position: absolute;
  top: 33px;
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.color.white};
  left: calc(50% - 4px);
  transform: rotate(45deg);
  border-top: ${({ theme }) => theme.stroke.light};
  border-left: ${({ theme }) => theme.stroke.light};
  z-index: ${({ theme }) => theme.positioning.modal};
`;

Button.defaultProps = {
  size: 'large',
  mode: 'primary',
  isIconOnly: false,
};
